import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, Typography, Button, Checkbox, FormControlLabel, Box, Alert, CircularProgress, Link as MuiLink, Fade, Paper } from '@mui/material';
import { supabase } from '../config/supabaseClient';
import { API_BASE_URL } from '../config/api';
import SecurityIcon from '@mui/icons-material/Security';
import PolicyIcon from '@mui/icons-material/Policy';
import GavelIcon from '@mui/icons-material/Gavel';

export default function AgbPopup() {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [error, setError] = useState('');
  const [userEmail, setUserEmail] = useState('');
  
  const [infoDialog, setInfoDialog] = useState(null);

  useEffect(() => {
    const checkAgbStatus = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.email) {
          setLoading(false);
          return;
        }

        const email = session.user.email;
        setUserEmail(email);

        const response = await fetch(`${API_BASE_URL}/api/employees/email/${email}`);
        if (response.ok) {
          const employeeData = await response.json();
          // If agbAccepted is undefined or false, we must show the popup
          if (!employeeData.agbAccepted) {
            setOpen(true);
          }
        }
      } catch (err) {
        console.error('Error checking AGB status:', err);
      } finally {
        setLoading(false);
      }
    };

    // Delay slightly to ensure auth state is loaded
    const timeout = setTimeout(checkAgbStatus, 1000);
    return () => clearTimeout(timeout);
  }, []);

  const handleSubmit = async () => {
    if (!agbAccepted) {
      setError('Sie müssen den Bedingungen zustimmen, um fortzufahren.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/employees/${userEmail}/agb`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!response.ok) {
        throw new Error('Fehler beim Speichern der Zustimmung.');
      }

      setOpen(false);
    } catch (err) {
      setError(err.message || 'Ein unerwarteter Fehler ist aufgetreten.');
      setSubmitting(false);
    }
  };

  if (loading) return null;

  return (
    <Dialog 
      open={open} 
      disableEscapeKeyDown 
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(10px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }
      }}
    >
      <Fade in={open} timeout={800}>
        <Paper elevation={24} sx={{ maxWidth: 600, width: '90%', p: { xs: 4, md: 6 }, borderRadius: 6, textAlign: 'center', border: '1px solid #e2e8f0' }}>
          
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mb: 4 }}>
            <Box sx={{ width: 64, height: 64, borderRadius: '50%', bgcolor: 'primary.50', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'primary.main' }}>
              <SecurityIcon sx={{ fontSize: 32 }} />
            </Box>
          </Box>
          
          <Typography variant="h4" fontWeight={900} color="#1e293b" gutterBottom>
            Aktualisierte Nutzungsbedingungen
          </Typography>
          
          <Typography variant="body1" color="text.secondary" paragraph sx={{ mb: 4, lineHeight: 1.7 }}>
            Um unsere Anwendung weiterhin nutzen zu können, müssen Sie unseren aktualisierten Richtlinien zur Datenverarbeitung und Sicherheit zustimmen. Dies dient dem Schutz Ihrer Daten und der Einhaltung gesetzlicher Vorgaben (DSGVO).
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4, textAlign: 'left', bgcolor: '#f8fafc', p: 3, borderRadius: 4, border: '1px solid #f1f5f9' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <GavelIcon color="primary" />
              <Typography variant="body2" fontWeight={600}>Allgemeine Geschäftsbedingungen (AGB)</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <PolicyIcon color="primary" />
              <Typography variant="body2" fontWeight={600}>Datenschutzerklärung & AVV (Art. 28 DSGVO)</Typography>
            </Box>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 3 }}>
              {error}
            </Alert>
          )}

          <FormControlLabel
            control={
              <Checkbox 
                checked={agbAccepted} 
                onChange={(e) => setAgbAccepted(e.target.checked)} 
                sx={{ '& .MuiSvgIcon-root': { fontSize: 28 } }}
              />
            }
            label={
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'left' }}>
                Ich bestätige, dass ich die <MuiLink component="button" variant="body2" onClick={() => setInfoDialog('agb')} underline="hover" fontWeight={700}>AGB</MuiLink>, die <MuiLink component="button" variant="body2" onClick={() => setInfoDialog('nutzung')} underline="hover" fontWeight={700}>Nutzungsbedingungen</MuiLink> und die <MuiLink component="button" variant="body2" onClick={() => setInfoDialog('datenschutz')} underline="hover" fontWeight={700}>Datenschutzerklärung</MuiLink> gelesen habe und diesen ausdrücklich zustimme.
              </Typography>
            }
            sx={{ mb: 4, alignItems: 'flex-start' }}
          />

          <Button 
            variant="contained" 
            fullWidth 
            size="large"
            onClick={handleSubmit}
            disabled={!agbAccepted || submitting}
            sx={{ 
              py: 2, 
              borderRadius: 3, 
              fontWeight: 800, 
              fontSize: '1.1rem',
              textTransform: 'none',
              boxShadow: '0 8px 16px rgba(59, 130, 246, 0.25)',
              '&:hover': {
                boxShadow: '0 12px 20px rgba(59, 130, 246, 0.35)',
              }
            }}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Zustimmen & Fortfahren'}
          </Button>

        </Paper>
      </Fade>

      <Dialog open={!!infoDialog} onClose={() => setInfoDialog(null)} maxWidth="sm" fullWidth>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="h6" fontWeight={800} gutterBottom>
            {infoDialog === 'agb' ? 'Allgemeine Geschäftsbedingungen (AGB)' : infoDialog === 'nutzung' ? 'Nutzungsbedingungen' : 'Datenschutzerklärung & AVV'}
          </Typography>
          
          <Box sx={{ maxHeight: '50vh', overflowY: 'auto', pr: 1, mt: 2 }}>
            {infoDialog === 'agb' && (
              <>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>§1 Geltungsbereich & Vertragsgegenstand</Typography>
                <Typography variant="body2" paragraph>
                  (1) Diese Allgemeinen Geschäftsbedingungen gelten für alle Verträge über die Nutzung der Software-as-a-Service (SaaS) Plattform, die zwischen dem Anbieter und dem Kunden (Unternehmer im Sinne des § 14 BGB) geschlossen werden.
                  <br />(2) Der Anbieter stellt die Software über das Internet zur Verfügung. Der Funktionsumfang ergibt sich aus der aktuellen Leistungsbeschreibung.
                </Typography>
                
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>§2 Verfügbarkeit & SLAs</Typography>
                <Typography variant="body2" paragraph>
                  (1) Der Anbieter gewährleistet eine Erreichbarkeit der Plattform von 99,5% im Jahresmittel. Ausgenommen hiervon sind angekündigte Wartungsarbeiten sowie Ausfälle durch höhere Gewalt.
                </Typography>
                
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>§3 Haftung & Gewährleistung</Typography>
                <Typography variant="body2" paragraph>
                  (1) Der Anbieter haftet unbeschränkt für Vorsatz und grobe Fahrlässigkeit. Für leichte Fahrlässigkeit haftet der Anbieter nur bei Verletzung wesentlicher Vertragspflichten, begrenzt auf den vertragstypisch vorhersehbaren Schaden.
                </Typography>
                
                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 2, fontWeight: 700, bgcolor: '#fef2f2', p: 1.5, borderRadius: 2 }}>
                  MUSTERTEXT: Dieser Platzhalter muss vor dem produktiven Einsatz durch anwaltlich geprüfte AGB ersetzt werden.
                </Typography>
              </>
            )}

            {infoDialog === 'nutzung' && (
              <>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>1. Zugelassene Nutzer</Typography>
                <Typography variant="body2" paragraph>
                  Die Plattform darf ausschließlich von autorisierten Mitarbeitern des jeweiligen Mandanten genutzt werden. Die Zugangsdaten sind streng vertraulich zu behandeln und dürfen nicht an Dritte weitergegeben werden.
                </Typography>

                <Typography variant="subtitle2" fontWeight={800} gutterBottom>2. Verbotene Handlungen</Typography>
                <Typography variant="body2" paragraph>
                  (1) Es ist untersagt, Schadsoftware hochzuladen, die Plattform durch maschinelle Zugriffe (Bots) zu überlasten oder Sicherheitsmechanismen zu umgehen.
                  <br />(2) Der Nutzer verpflichtet sich, keine illegalen, rassistischen, beleidigenden oder datenschutzrechtlich bedenklichen Inhalte hochzuladen.
                </Typography>

                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 2, fontWeight: 700, bgcolor: '#fef2f2', p: 1.5, borderRadius: 2 }}>
                  MUSTERTEXT: Dieser Platzhalter muss vor dem produktiven Einsatz durch rechtsgültige Nutzungsbedingungen ersetzt werden.
                </Typography>
              </>
            )}

            {infoDialog === 'datenschutz' && (
              <>
                <Typography variant="subtitle2" fontWeight={800} gutterBottom>I. Datenschutzerklärung (Art. 13 DSGVO)</Typography>
                <Typography variant="body2" paragraph>
                  Der Schutz Ihrer Daten ist uns wichtig. Wir verarbeiten Ihre personenbezogenen Daten (z.B. Name, E-Mail-Adresse, IP-Adresse) ausschließlich auf Grundlage der DSGVO. Ihre Daten werden für den Login, die Profilverwaltung und zur Erbringung der vertraglichen SaaS-Dienstleistung genutzt. 
                </Typography>

                <Typography variant="subtitle2" fontWeight={800} gutterBottom>II. Auftragsverarbeitungsvertrag (Art. 28 DSGVO)</Typography>
                <Typography variant="body2" paragraph>
                  (1) Da wir in Ihrem Auftrag personenbezogene Daten Ihrer Mitarbeiter und Kunden auf unseren Servern speichern, fungieren wir als Auftragsverarbeiter.
                  <br />(2) <b>Technisch-organisatorische Maßnahmen (TOMs):</b> Wir sichern Ihre Daten durch Transportverschlüsselung (SSL/TLS), strenge Zugriffskontrollen, verschlüsselte Datenbanken und regelmäßige Backups.
                  <br />(3) Die Daten werden ausschließlich in Rechenzentren innerhalb der Europäischen Union (EU) gehostet.
                </Typography>

                <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 2, fontWeight: 700, bgcolor: '#fef2f2', p: 1.5, borderRadius: 2 }}>
                  MUSTERTEXT: Bitte erstellen Sie eine vollständige Datenschutzerklärung sowie einen AVV (inkl. detaillierter TOMs) über einen Rechtstexte-Generator wie z.B. eRecht24.
                </Typography>
              </>
            )}
          </Box>
          <Button variant="contained" fullWidth onClick={() => setInfoDialog(null)} sx={{ mt: 2, borderRadius: 2, fontWeight: 700 }}>
            Schließen
          </Button>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
